import {
  ENDPOINTS,
  VerificationQueueRequestSchema,
  VerifyFieldRequestSchema,
  VerifyFieldsBulkRequestSchema,
  type VerificationQueueRequest,
  type VerificationQueueResponse,
  type VerifyFieldRequest,
  type VerifyFieldResponse,
  type VerifyFieldsBulkRequest,
  type VerifyFieldsBulkResponse,
} from '@clm/shared-types';

import { apiGet, apiPost, type Query } from './client';

/**
 * `verification-queue` and `verify-field` — the FR-07 pair, wrapped for the
 * browser. `ingest.ts` is the precedent: the path comes from the registry, the
 * request is `.parse`d before it leaves the browser, and the response is
 * **parsed** rather than asserted.
 *
 * ## The queue is a GET, so the request object has to be flattened
 *
 * `registry.ts` folds path, query and body into one validated object and the
 * per-endpoint `notes` say which half a field belongs to; for
 * `verification-queue` that is *"all query parameters"*. `apps/api/src/http/decode.ts`
 * defines exactly one encoding for that:
 *
 *   - dotted keys for the nested page object — `page.limit=20`, `page.cursor=…`;
 *   - a **repeated** key for an array — `status=Pending&status=Rejected`.
 *     Comma-joining is explicitly not supported, and would be wrong anyway;
 *   - booleans as the literal strings `true` / `false`, coerced back *by schema*
 *     on arrival, so `2024` in a text field never becomes a number.
 *
 * Nothing is invented here: the wrapper writes the encoding the decoder reads.
 */

const QUEUE_ENDPOINT = ENDPOINTS['verification-queue'];
const VERIFY_ENDPOINT = ENDPOINTS['verify-field'];
const BULK_ENDPOINT = ENDPOINTS['verify-fields'];

/** Flatten a validated queue request into the query the decoder expands. */
export function verificationQueueQuery(request: VerificationQueueRequest): Query {
  const query: Record<string, string | number | boolean | readonly string[]> = {};

  if (request.contract_key !== undefined) query['contract_key'] = request.contract_key;
  if (request.party_key !== undefined) query['party_key'] = request.party_key;
  // CLM-86 — the four row narrowings the queue surface sends. `tags` is a repeated
  // key like `status` below; the other two are one exact value each.
  if (request.tags !== undefined) query['tags'] = [...request.tags];
  if (request.field_name !== undefined) query['field_name'] = request.field_name;
  if (request.contract_type !== undefined) query['contract_type'] = request.contract_type;
  if (request.high_stakes_only !== undefined) {
    query['high_stakes_only'] = request.high_stakes_only;
  }
  if (request.escalated_only !== undefined) query['escalated_only'] = request.escalated_only;
  // A repeated key, not a comma-joined string: a single-element array still
  // arrives as an array because coerceBySchema wraps a lone value for a
  // ZodArray field.
  if (request.status !== undefined) query['status'] = [...request.status];
  if (request.sort !== undefined) query['sort'] = request.sort;
  if (request.page?.limit !== undefined) query['page.limit'] = request.page.limit;
  if (request.page?.cursor !== undefined) query['page.cursor'] = request.page.cursor;

  return query;
}

export async function fetchVerificationQueue(
  request: VerificationQueueRequest,
  signal?: AbortSignal,
): Promise<VerificationQueueResponse> {
  // `.strict()`, so an invented or scope-shaped key is a ZodError here rather
  // than a parameter on the wire. buildUrl's FORBIDDEN_PARAMS guard is the
  // second line of the same defence.
  const validated = VerificationQueueRequestSchema.parse(request);
  const raw = await apiGet<unknown>(QUEUE_ENDPOINT.path, {
    query: verificationQueueQuery(validated),
    ...(signal === undefined ? {} : { signal }),
  });
  return QUEUE_ENDPOINT.response.parse(raw);
}

/**
 * Substitute a path parameter into a registry path.
 *
 * `encodeURIComponent` rather than raw interpolation, because the value is data
 * — a verification id or a contract key — and a path that a value can break out
 * of is a path a value can redirect.
 */
export function substitutePath(path: string, params: Readonly<Record<string, string>>): string {
  return path.replace(/\{([a-z0-9_]+)\}/g, (_whole, name: string) => {
    const value = params[name];
    if (value === undefined) {
      throw new Error(`Cannot build "${path}": no value supplied for {${name}}.`);
    }
    return encodeURIComponent(value);
  });
}

/**
 * `verify-field` — the human half of the CR-01 evidence chain.
 *
 * `verification_id` goes in the path **and** in the body, and that redundancy is
 * deliberate on the contract's side: decode.ts cross-checks the two and answers
 * `validation_failed` on a mismatch rather than letting the path silently "fix"
 * a mis-wired front end that verified the wrong row. So the wrapper sends the
 * id from the *validated body* in both places — one source, no way to disagree.
 */
export async function verifyField(
  request: VerifyFieldRequest,
  signal?: AbortSignal,
): Promise<VerifyFieldResponse> {
  const body = VerifyFieldRequestSchema.parse(request);
  const path = substitutePath(VERIFY_ENDPOINT.path, { verification_id: body.verification_id });
  const raw = await apiPost<unknown>(path, body, signal === undefined ? {} : { signal });
  return VERIFY_ENDPOINT.response.parse(raw);
}

/**
 * `verify-fields` — CLM-86, mass Confirm / Reject over selected queue rows.
 *
 * Every item carries the `seen_extracted_value` the row **showed** (the
 * `VerifyPanel` rule, applied N times): the API cross-checks it per row and
 * answers `not_pending` for a stale one rather than attesting to a value nobody
 * read. The response is per row — a mixed result is the success shape, and the
 * surface renders it row by row.
 */
export async function verifyFieldsBulk(
  request: VerifyFieldsBulkRequest,
  signal?: AbortSignal,
): Promise<VerifyFieldsBulkResponse> {
  const body = VerifyFieldsBulkRequestSchema.parse(request);
  const raw = await apiPost<unknown>(BULK_ENDPOINT.path, body, signal === undefined ? {} : { signal });
  return BULK_ENDPOINT.response.parse(raw);
}
